import { toWorldSnapshot } from '@runkr/schema'
import type { RacePhase, RaceState } from '@runkr/schema'
import { createRngStreams } from '@runkr/sim'
import type { WorldSnapshot } from '@runkr/sim'

/** One runner's room data (not simulated), by player index. */
export interface RosterEntry {
  sessionId: string
  index: number
  displayName: string
  charId: number
  connected: boolean
}

/** One row of the final standings (`ResultSchema` as plain data). */
export interface ResultRow {
  sessionId: string
  index: number
  displayName: string
  charId: number
  placement: number
  finished: boolean
  finishMs: number
  progress: number
  coins: number
  /** Earned this race (T2.2): the results screen's +XP / +coins. */
  xp: number
  coinsEarned: number
}

/**
 * The room state at one patch as plain data (PHASE1 T1.7): safe to buffer, delay (`?lag`) and
 * compare, never aliasing live schema objects. `world` is `toWorldSnapshot` over an empty base
 * (the client has no rng, bot memory or crate timers, D-033); crates come as `crateTaken`.
 */
export interface RoomSnapshot {
  phase: RacePhase
  /** Room tick: negative during the countdown, GO = `countdownEndTick` (0). */
  tick: number
  countdownEndTick: number
  seed: number
  trackId: string
  code: string
  hostSessionId: string
  /** By player index. */
  roster: RosterEntry[]
  world: WorldSnapshot
  crateTaken: boolean[]
  results: ResultRow[]
}

const EMPTY_BASE: WorldSnapshot = {
  tick: 0,
  players: [],
  projectiles: [],
  traps: [],
  crates: [],
  rng: createRngStreams(0),
  finishOrder: [],
  platformState: [],
  coinsTaken: [],
  bots: [],
  nextId: 0,
}

/** Copy the room state. Throws only on a broken roster (player indices with a hole). */
export function roomSnapshot(state: RaceState): RoomSnapshot {
  const roster: RosterEntry[] = []
  state.players.forEach((p, sessionId) => {
    roster[p.index] = {
      sessionId,
      index: p.index,
      displayName: p.displayName,
      charId: p.charId,
      connected: p.connected,
    }
  })
  return {
    phase: state.phase,
    tick: state.tick,
    countdownEndTick: state.countdownEndTick,
    seed: state.seed,
    trackId: state.trackId,
    code: state.code,
    hostSessionId: state.hostSessionId,
    roster,
    world: toWorldSnapshot(state, EMPTY_BASE),
    crateTaken: state.crateTaken.map((v) => v),
    results: state.results.map((r) => ({
      sessionId: r.sessionId,
      index: r.index,
      displayName: r.displayName,
      charId: r.charId,
      placement: r.placement,
      finished: r.finished,
      finishMs: r.finishMs,
      progress: r.progress,
      coins: r.coins,
      xp: r.xp,
      coinsEarned: r.coinsEarned,
    })),
  }
}

/** The local player's index, or -1 when `sessionId` has no runner. */
export function localIndexOf(snap: RoomSnapshot, sessionId: string): number {
  return snap.roster.find((r) => r?.sessionId === sessionId)?.index ?? -1
}
